/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { RefObject, useCallback, useEffect, useRef, useState } from "react";

interface AnimatedClosure<R extends HTMLElement> {
  closing: boolean;
  ref: RefObject<R>;
  startClose: VoidFunction;
}

export const useAnimatedClosure = <R extends HTMLElement>(
  onClose: VoidFunction
): AnimatedClosure<R> => {
  const [closing, setClosing] = useState(false);

  const ref = useRef<R | null>(null);

  const startClose = useCallback((): void => {
    setClosing(true);
    let hardClose = (): void => {
      onClose();
      setClosing(false);
      ref.current!.removeEventListener("transitionend", hardClose);
    };
    ref.current!.addEventListener("transitionend", hardClose);
  }, [onClose]);

  return { closing, ref, startClose };
};
